import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/contexts/I18nContext';
import { OnboardingProgressBar } from './OnboardingProgressBar';
import logo from '@/assets/images/logo/logo.svg';

interface OnboardingShellProps {
  step: number;
  totalSteps: number;
  /** Heading + subheading shown above the content. Omit when the step renders its own heading. */
  title?: string;
  subtitle?: string;
  /** Fixed viewport footer (primary CTA + optional secondary action). */
  stickyFooter?: ReactNode;
  children: ReactNode;
}

export const OnboardingShell = ({
  step,
  totalSteps,
  title,
  subtitle,
  stickyFooter,
  children,
}: OnboardingShellProps) => {
  const { t } = useI18n();

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-sossiety-dark">
      {/* Top bar with logo */}
      <header
        className="flex-shrink-0 border-b border-white/10 px-6"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <div className="mx-auto flex h-14 max-w-[560px] items-center">
          <img src={logo} alt="sOSSiety" className="h-7 w-auto" />
        </div>
      </header>

      {/* Scrollable, centered content */}
      <main className={cn('flex-1 overflow-y-auto overscroll-contain px-6', stickyFooter && 'pb-40')}>
        <div className="mx-auto max-w-[560px] pt-8">
          <OnboardingProgressBar totalSteps={totalSteps} currentStep={step} />

          <p className="mt-8 text-xs font-semibold uppercase tracking-wider text-white/40">
            {t('onboarding.step', { current: step, total: totalSteps })}
          </p>
          {title && <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-white">{title}</h2>}
          {subtitle && <p className="mt-2 leading-relaxed text-white/50">{subtitle}</p>}

          <div className="mt-8">{children}</div>
        </div>
      </main>

      {stickyFooter && (
        <footer
          className="fixed inset-x-0 bottom-0 z-20 bg-gradient-to-t from-sossiety-dark via-[#160c0c] to-transparent px-6 pt-4"
          style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto max-w-[560px]">{stickyFooter}</div>
        </footer>
      )}
    </div>
  );
};
